import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import CircuitMap from '../components/CircuitMap';
import Icon from '../components/Icon';
import { F1_DRIVERS, driverImgUrl } from '../constants/f1Drivers';

const SEASON = 2026;

const TEAM_COLORS = {
  'Red Bull Racing': '#3671C6', 'Ferrari': '#E8002D', 'Mercedes': '#27F4D2',
  'McLaren': '#FF8000', 'Aston Martin': '#229971', 'Alpine': '#FF87BC',
  'Williams': '#64C4FF', 'Racing Bulls': '#6692FF', 'Audi': '#52E252',
  'Haas': '#B6BABD', 'Haas F1 Team': '#B6BABD', 'Cadillac': '#CC0000',
};
const teamColor = (t) => TEAM_COLORS[t] || '#71717a';

const driverImgByNumber = (number) => {
  const d = F1_DRIVERS.find(d => String(d.number) === String(number));
  return d ? driverImgUrl(d.img) : null;
};

const useScrollReveal = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
};

const AnimatedBar = ({ value, max, color, delay = 0 }) => {
  const [w, setW] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setW(max > 0 ? (value / max) * 100 : 0), delay + 150);
    return () => clearTimeout(t);
  }, [value, max, delay]);
  return (
    <div className="h-1 rounded-full bg-zinc-800 overflow-hidden mt-1" style={{ minWidth: 40 }}>
      <div className="h-full rounded-full transition-all duration-700 ease-out"
        style={{ width: `${w}%`, background: `linear-gradient(90deg, ${color}66, ${color})` }} />
    </div>
  );
};

const CANCELLED_RACES = new Set(['Bahrain', 'Saudi Arabia']);

const fisherYates = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const pickHeroDrivers = () => {
  const shuffled = fisherYates(F1_DRIVERS);
  return [shuffled[0], shuffled[1]];
};

const useCountdown = (target) => {
  const [remaining, setRemaining] = useState(null);
  useEffect(() => {
    if (!target) return;
    const tick = () => {
      const diff = new Date(target) - new Date();
      if (diff <= 0) { setRemaining({ d: 0, h: 0, m: 0, s: 0 }); return; }
      setRemaining({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);
  return remaining;
};

const CountUnit = ({ value, label }) => (
  <div className="flex flex-col items-center bg-zinc-950 border border-zinc-800 rounded-xl px-3 py-2.5 min-w-[56px]">
    <span className="text-2xl font-black text-white tabular-nums leading-none">{String(value).padStart(2, '0')}</span>
    <span className="text-[10px] text-zinc-500 uppercase tracking-widest mt-1">{label}</span>
  </div>
);

const SessionRow = ({ label, session }) => {
  if (!session?.date) return null;
  const d = new Date(`${session.date}T${session.time || '00:00:00Z'}`);
  return (
    <div className="flex justify-between items-center py-1.5 border-b border-zinc-800 last:border-0">
      <span className="text-zinc-400 text-sm">{label}</span>
      <span className="text-white text-sm font-medium">
        {d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
        {' · '}
        {d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
      </span>
    </div>
  );
};

const BG_NUMBERS = ['1', '44', '16', '63', '4', '14', '81', '55', '11', '10'];

const GAMES = [
  {
    id: 'lightspeed',
    to: '/lightspeed',
    title: 'LightSpeed',
    label: 'RÉFLEXES',
    description: '5 feux s\'allument puis s\'éteignent. Appuie sur GO le plus vite possible. Chaque milliseconde compte.',
    tiers: [
      { label: '< 0.3s', xp: '1 000 XP', color: '#e10600' },
      { label: '< 0.4s', xp: '400 XP',   color: '#f97316' },
      { label: '< 1.0s', xp: '100 XP',   color: '#a3e635' },
      { label: 'Faux départ', xp: '−1 000 XP', color: '#52525b' },
    ],
    accentColor: '#e10600',
    visual: (
      <div className="flex items-center justify-center gap-2.5 py-1">
        {[1,2,3,4,5].map(i => (
          <div key={i} className={`w-7 h-7 rounded-full border-2 ${i <= 3 ? 'bg-red-600 border-red-400' : 'bg-zinc-800 border-zinc-700'}`}
            style={i <= 3 ? { boxShadow: '0 0 10px 3px rgba(225,6,0,0.5)' } : {}} />
        ))}
      </div>
    ),
  },
  {
    id: 'cirquiz',
    to: '/quiz',
    title: 'CirQuiz',
    label: 'QUIZ',
    description: 'Un tracé de circuit s\'affiche, 3 GPs proposés. 30 secondes pour identifier le maximum. Mauvaise réponse = +2s.',
    tiers: [
      { label: '≥ 16', xp: '1 200 XP', color: '#22c55e' },
      { label: '≥ 10', xp: '500 XP',   color: '#86efac' },
      { label: '≥ 4',  xp: '150 XP',   color: '#a3e635' },
    ],
    accentColor: '#22c55e',
    visual: (
      <div className="flex items-center justify-center gap-1.5 py-1">
        {['🇦🇺','🇲🇨','🇬🇧','🇮🇹','🇯🇵'].map((flag, i) => (
          <span key={i} className="text-xl" style={{ opacity: i === 2 ? 1 : 0.3 }}>{flag}</span>
        ))}
      </div>
    ),
  },
  {
    id: 'podiumquiz',
    to: '/podium',
    title: 'PodiumQuiz',
    label: 'PODIUMS',
    description: 'Un GP (2022–2025) s\'affiche. Reconstitue le podium P1/P2/P3 en glissant les pilotes. 0/6 = Game Over.',
    tiers: [
      { label: '≥ 36 pts', xp: '1 500 XP', color: '#facc15' },
      { label: '≥ 18 pts', xp: '600 XP',   color: '#fb923c' },
      { label: '≥ 6 pts',  xp: '150 XP',   color: '#a3e635' },
    ],
    accentColor: '#facc15',
    visual: (
      <div className="flex items-end justify-center gap-2 py-1">
        {[
          { pos: 'P2', h: 28, color: '#94a3b8' },
          { pos: 'P1', h: 40, color: '#facc15' },
          { pos: 'P3', h: 20, color: '#fb923c' },
        ].map(({ pos, h, color }) => (
          <div key={pos} className="flex flex-col items-center gap-0.5">
            <span className="text-[9px] font-black" style={{ color }}>{pos}</span>
            <div className="w-9 rounded-t-sm" style={{ height: h, backgroundColor: `${color}2a`, border: `1.5px solid ${color}55` }} />
          </div>
        ))}
      </div>
    ),
  },
  {
    id: 'grille',
    to: '/grille',
    title: 'Grille de Départ',
    label: 'QUALIFICATIONS',
    description: 'Un GP de 2022 à 2025 s\'affiche. Remets les pilotes dans l\'ordre exact de la grille de départ (qualifications). 100 XP par bonne position, 1 défi par jour.',
    tiers: [
      { label: '20/20', xp: '2 000 XP', color: '#06b6d4' },
      { label: '15/20', xp: '1 500 XP', color: '#22d3ee' },
      { label: '10/20', xp: '1 000 XP', color: '#67e8f9' },
      { label: '1/20',  xp: '100 XP',   color: '#6b7280' },
    ],
    accentColor: '#06b6d4',
    visual: (
      <div className="flex flex-col gap-2 py-1">
        {[{pos:'P1',c:'#facc15'},{pos:'P2',c:'#94a3b8'},{pos:'P3',c:'#fb923c'},{pos:'P4',c:'#6b7280'}].map(({pos,c}) => (
          <div key={pos} className="flex items-center gap-2">
            <span className="text-[10px] font-black w-5" style={{color:c}}>{pos}</span>
            <div className="h-2 rounded-full flex-1" style={{backgroundColor:`${c}33`, border:`1px solid ${c}66`}} />
          </div>
        ))}
      </div>
    ),
  },
  {
    id: 'guess',
    to: '/guess',
    title: 'Qui Suis-Je ?',
    label: 'PILOTE',
    description: 'Un pilote F1 est tiré au sort. Des indices te sont donnés un par un. Plus tu trouves tôt, plus tu gagnes de XP. Joue autant que tu veux !',
    tiers: [
      { label: '1er indice', xp: '1 000 XP', color: '#a855f7' },
      { label: '2e indice',  xp: '900 XP',   color: '#c084fc' },
      { label: 'Trouvé',     xp: '≥ 100 XP', color: '#6b7280' },
    ],
    accentColor: '#a855f7',
    visual: (
      <div className="flex flex-col items-center justify-center gap-2 py-1">
        <div className="text-3xl">🕵️</div>
        <div className="flex gap-1">
          {[1,2,3,4,5,6].map(i => (
            <div key={i} className="h-1.5 w-5 rounded-full" style={{ backgroundColor: i <= 2 ? '#a855f7' : '#3f3f46' }} />
          ))}
        </div>
      </div>
    ),
  },
  {
    id: 'gap',
    to: '/gap',
    title: 'Le Gap',
    label: 'ÉCART',
    description: 'Deux pilotes d\'une même course. Estime l\'écart de temps entre eux en secondes. 5 manches par course. Joue autant que tu veux !',
    tiers: [
      { label: '±10%',  xp: '1 000 XP', color: '#f59e0b' },
      { label: '±25%',  xp: '800 XP',   color: '#fbbf24' },
      { label: '±50%',  xp: '600 XP',   color: '#fcd34d' },
    ],
    accentColor: '#f59e0b',
    visual: (
      <div className="flex flex-col items-center justify-center gap-2 py-1">
        <div className="text-3xl">⏱️</div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-amber-400 font-black">P3</span>
          <span className="text-zinc-600 font-black">↔</span>
          <span className="text-zinc-400 font-black">P7</span>
        </div>
      </div>
    ),
  },
  {
    id: 'lead',
    to: '/lead',
    title: 'Qui Menait ?',
    label: 'LEADERS',
    description: 'Un tour de course s\'affiche. Qui était en P1 à ce moment-là ? 4 choix, 10 questions par session. Données réelles, illimité.',
    tiers: [
      { label: '10/10', xp: '1 000 XP', color: '#3b82f6' },
      { label: '7/10',  xp: '700 XP',   color: '#60a5fa' },
      { label: '4/10',  xp: '400 XP',   color: '#93c5fd' },
    ],
    accentColor: '#3b82f6',
    visual: (
      <div className="flex flex-col items-center justify-center gap-2 py-1">
        <div className="text-3xl">🏁</div>
        <div className="flex items-center gap-1 text-[10px]">
          <span className="text-blue-400 font-black">Tour 42</span>
          <span className="text-zinc-500">·</span>
          <span className="text-zinc-400">Qui mène ?</span>
        </div>
      </div>
    ),
  },
  {
    id: 'saison',
    to: '/saison',
    title: 'Champion',
    label: 'SAISON',
    description: 'Qui remporte le championnat ? Fais ton pronostic en début de saison et suis l\'évolution au fil des GP.',
    tiers: [
      { label: 'Dès le départ', xp: '2 000 XP', color: '#a855f7' },
      { label: 'Correction',    xp: '500 XP',   color: '#7c3aed' },
    ],
    accentColor: '#a855f7',
    visual: (
      <div className="flex items-center justify-center gap-2 py-1">
        <Icon name="trophy" size={32} />
        <div className="text-left leading-tight">
          <p className="text-white font-black text-xs">WCC · WDC</p>
          <p className="text-zinc-500 text-xs">2026</p>
        </div>
      </div>
    ),
  },
];


// ── F1 Standings Ticker ───────────────────────────────────────────────────────
const StandingsTicker = ({ standings }) => {
  const raw = standings?.drivers?.slice(0, 12) || F1_DRIVERS.slice(0, 12);
  const items = raw.map((d, i) => ({
    code:     d.code || d.name?.split(' ').pop() || '?',
    team:     d.team,
    points:   d.points,
    position: d.position ?? (i + 1),
  }));
  const doubled = [...items, ...items];
  return (
    <div className="border-y border-zinc-800/50 bg-zinc-950 py-2.5 overflow-hidden relative select-none">
      <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-zinc-950 to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-zinc-950 to-transparent z-10 pointer-events-none" />
      <div className="animate-ticker flex gap-8" style={{ width: 'max-content' }}>
        {doubled.map((d, i) => {
          const color = teamColor(d.team);
          return (
            <span key={i} className="flex items-center gap-1.5 shrink-0">
              <span className="text-[10px] font-bold text-zinc-700">P{d.position}</span>
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} />
              <span className="text-[11px] font-black uppercase tracking-wider" style={{ color }}>{d.code}</span>
              {d.points != null && <span className="text-[10px] text-zinc-600 font-mono tabular-nums">{d.points}pts</span>}
              <span className="text-zinc-800 ml-2 text-xs">·</span>
            </span>
          );
        })}
      </div>
    </div>
  );
};

// ── How it works (scroll-reveal staggered) ────────────────────────────────────
const HowItWorksSection = () => {
  const [ref, visible] = useScrollReveal();
  const steps = [
    { step: '01', icon: 'calendar', title: 'Choisis ton GP', desc: 'Sélectionne le prochain Grand Prix dans le calendrier et clique sur "Faire mes pronos".' },
    { step: '02', icon: 'target',   title: 'Pose tes pronostics', desc: 'Vainqueur, podium, pole, fastest lap, crashs, DNF... Remplis ce que tu veux, tout est optionnel.' },
    { step: '03', icon: 'flame',    title: 'Écrase tes potes', desc: 'Les scores tombent après la course. Qui a le meilleur nez ? Le classement ne ment pas.' },
  ];
  return (
    <section ref={ref} className="max-w-6xl mx-auto px-4 py-16 border-t border-zinc-900">
      <div className="flex items-center gap-3 mb-10"
        style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)', transition: 'all 500ms' }}>
        <div className="w-1 h-6 bg-f1red rounded-full" />
        <h2 className="section-title">Comment ça marche ?</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {steps.map((s, i) => (
          <div key={s.step} className="card-hover group"
            style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(36px)', transition: `all 600ms cubic-bezier(0.16,1,0.3,1) ${i * 130}ms` }}>
            <div className="flex items-start gap-4">
              <span className="text-5xl font-black text-zinc-800 group-hover:text-f1red/30 transition-colors leading-none shrink-0">{s.step}</span>
              <div>
                <div className="mb-2"><Icon name={s.icon} size={24} className="text-f1red" /></div>
                <h3 className="font-black text-white text-lg mb-1">{s.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{s.desc}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const ChevronIcon = ({ dir }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d={dir === 'left' ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6'} />
  </svg>
);

const BoxBoxSection = () => {
  const [activeIdx, setActiveIdx] = useState(0);
  const touchStartX = React.useRef(null);
  const N = GAMES.length;

  const goTo = (idx) => setActiveIdx(Math.max(0, Math.min(N - 1, idx)));
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd   = (e) => {
    if (touchStartX.current === null) return;
    const delta = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(delta) > 40) goTo(activeIdx + (delta > 0 ? 1 : -1));
    touchStartX.current = null;
  };

  return (
    <section className="py-12 border-t border-zinc-900">
      {/* Header */}
      <div className="max-w-6xl mx-auto px-4 flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="speed-divider w-14 shrink-0" />
          <div>
            <p className="f1-label mb-0.5">Mini-jeux F1</p>
            <h2 className="section-title leading-none">Box-Box</h2>
          </div>
        </div>
        <Link to="/box-box" className="text-xs font-bold uppercase tracking-wider text-zinc-500 hover:text-f1red transition-colors">
          Voir tout →
        </Link>
      </div>

      {/* Slider — full-bleed overflow-hidden, no padding here */}
      <div className="overflow-hidden">
        {/*
          Classic bulletproof slider pattern (works on Safari iOS):
          - Track width = N × 100% of the viewport/container
          - Each slide = (100/N)% of the track = 100% of container
          - translateX step = (100/N)% of the track
        */}
        <div
          className="flex select-none"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          style={{
            width: `${N * 100}%`,
            transform: `translateX(-${activeIdx * (100 / N)}%)`,
            transition: 'transform 0.35s cubic-bezier(0.4,0,0.2,1)',
            touchAction: 'pan-y',
          }}
        >
          {GAMES.map((game) => (
            <div key={game.id} className="shrink-0 px-4" style={{ width: `${100 / N}%` }}>
              <div className="rounded-lg border border-zinc-800/80 bg-[#0e0e12] overflow-hidden max-w-2xl mx-auto">
                {/* Accent bar */}
                <div className="h-[3px]" style={{ background: `linear-gradient(90deg, ${game.accentColor} 0%, transparent 60%)` }} />

                <div className="p-5 sm:p-7 md:flex md:gap-8 md:items-center md:p-8">
                  {/* Text block */}
                  <div className="flex-1 min-w-0">
                    <span className="text-[10px] font-black tracking-[0.3em] uppercase block mb-1"
                      style={{ color: game.accentColor }}>{game.label}</span>
                    <h3 className="text-3xl sm:text-4xl font-black text-white leading-none mb-3">{game.title}</h3>
                    <p className="text-zinc-400 text-sm leading-relaxed mb-4">{game.description}</p>
                    <div className="flex flex-wrap gap-1.5 mb-5">
                      {game.tiers.map(t => (
                        <span key={t.label} className="text-[11px] font-bold px-2 py-0.5 rounded-sm border"
                          style={{ color: t.color, borderColor: `${t.color}44`, background: `${t.color}10` }}>
                          {t.label} → {t.xp}
                        </span>
                      ))}
                    </div>
                    <Link to={game.to} className="btn-primary w-full sm:w-auto px-8 py-3 text-center block sm:inline-block">
                      Jouer →
                    </Link>
                  </div>

                  {/* Visual — hidden on mobile, visible on md+ */}
                  <div className="hidden md:flex shrink-0 w-48 items-center justify-center py-4">
                    {game.visual}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Nav: ◀ dots ▶ */}
      <div className="flex items-center justify-center gap-4 mt-5">
        <button onClick={() => goTo(activeIdx - 1)} disabled={activeIdx === 0}
          className="w-9 h-9 rounded-sm bg-zinc-900 border border-zinc-700 text-f1red
                     hover:bg-zinc-800 transition-all active:scale-90
                     disabled:opacity-20 disabled:cursor-not-allowed flex items-center justify-center">
          <ChevronIcon dir="left" />
        </button>
        <div className="flex items-center gap-2">
          {GAMES.map((g, i) => (
            <button key={g.id} onClick={() => goTo(i)}
              style={{
                width: i === activeIdx ? 24 : 8, height: 4, borderRadius: 2,
                background: i === activeIdx ? g.accentColor : '#3f3f46',
                transition: 'width 0.25s ease, background 0.25s ease',
              }} />
          ))}
        </div>
        <button onClick={() => goTo(activeIdx + 1)} disabled={activeIdx === N - 1}
          className="w-9 h-9 rounded-sm bg-zinc-900 border border-zinc-700 text-f1red
                     hover:bg-zinc-800 transition-all active:scale-90
                     disabled:opacity-20 disabled:cursor-not-allowed flex items-center justify-center">
          <ChevronIcon dir="right" />
        </button>
      </div>
    </section>
  );
};

// ── Leaderboard podium ──────────────────────────────────────────────────────
const PODIUM_HEIGHTS = [96, 128, 72]; // P2, P1, P3
const PODIUM_ORDER   = [1, 0, 2];     // renders P2, P1, P3

const PodiumAvatar = ({ user }) => {
  const [failed, setFailed] = useState(false);
  const initials = (user?.username || '?').slice(0, 2).toUpperCase();
  if (user?.avatar && !failed)
    return <img src={user.avatar} alt={user.username}
      className="w-full h-full object-cover" onError={() => setFailed(true)} />;
  return <div className="w-full h-full flex items-center justify-center font-black text-sm"
    style={{ color: '#e10600' }}>{initials}</div>;
};

const LeaderboardPodium = ({ leaderboard }) => {
  const [ref, visible] = useScrollReveal();
  const MEDALS = [
    { rank: 1, color: '#FFD600', glow: 'rgba(255,214,0,0.35)', label: '🥇 P1' },
    { rank: 2, color: '#A8A8B3', glow: 'rgba(168,168,179,0.25)', label: '🥈 P2' },
    { rank: 3, color: '#CD7F32', glow: 'rgba(205,127,50,0.25)', label: '🥉 P3' },
  ];

  return (
    <section ref={ref} className="max-w-6xl mx-auto px-4 py-16 border-t border-zinc-900">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-1 h-6 bg-f1red rounded-full" />
          <h2 className="section-title">Top du classement</h2>
        </div>
        <Link to="/leaderboard" className="text-sm text-zinc-400 hover:text-white transition-colors">
          Voir tout →
        </Link>
      </div>

      {/* Podium visual */}
      <div className="flex items-end justify-center gap-3 sm:gap-5 mb-6">
        {PODIUM_ORDER.map((dataIdx) => {
          const p = leaderboard[dataIdx];
          if (!p) return null;
          const u = p.user || p;
          const rank = dataIdx + 1;
          const med = MEDALS[dataIdx];
          const blockH = PODIUM_HEIGHTS[PODIUM_ORDER.indexOf(dataIdx)];
          const delay = dataIdx === 0 ? 200 : dataIdx === 1 ? 0 : 400;

          return (
            <Link key={u._id} to={`/joueurs/${u._id}`}
              className="flex flex-col items-center gap-2 group transition-all duration-500"
              style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)', transitionDelay: `${delay}ms` }}>

              {/* Score badge */}
              <span className="text-xs font-black px-2 py-0.5 rounded-full border"
                style={{ color: med.color, borderColor: med.color + '55', background: med.color + '15' }}>
                {p.totalScore} pts
              </span>

              {/* Avatar circle */}
              <div className="rounded-2xl overflow-hidden border-2 transition-transform group-hover:scale-105"
                style={{ width: rank === 1 ? 64 : 52, height: rank === 1 ? 64 : 52, borderColor: med.color + '88', boxShadow: `0 0 20px ${med.glow}` }}>
                <PodiumAvatar user={u} />
              </div>

              {/* Username */}
              <p className="font-black text-white text-sm text-center truncate max-w-[80px] group-hover:text-f1red transition-colors leading-tight">
                {u.username}
              </p>

              {/* Podium block */}
              <div className="w-20 sm:w-24 rounded-t-xl flex items-center justify-center transition-all"
                style={{
                  height: visible ? blockH : 0,
                  background: `linear-gradient(180deg, ${med.color}20, ${med.color}08)`,
                  border: `1px solid ${med.color}33`,
                  transitionProperty: 'height', transitionDuration: '700ms', transitionDelay: `${delay + 100}ms`, transitionTimingFunction: 'cubic-bezier(0.4,0,0.2,1)',
                }}>
                <span className="text-xl font-black" style={{ color: med.color }}>{med.label.slice(0, 2)}</span>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Horizontal rank list for rest of top 3 info */}
      <div className="grid grid-cols-3 gap-3">
        {PODIUM_ORDER.map((dataIdx) => {
          const p = leaderboard[dataIdx];
          if (!p) return null;
          const u = p.user || p;
          const med = MEDALS[dataIdx];
          return (
            <div key={u._id} className="text-center rounded-xl border border-zinc-800 bg-zinc-900/50 py-2 px-1"
              style={{ borderColor: med.color + '22' }}>
              <p className="text-[10px] text-zinc-500">{u.favoriteDriver?.name || '—'}</p>
              <p className="text-xs font-black" style={{ color: med.color }}>{p.totalScore} pts</p>
            </div>
          );
        })}
      </div>
    </section>
  );
};

// ── F1 Standings Preview ─────────────────────────────────────────────────────
const StandingsPreview = ({ standings }) => {
  const [ref, visible] = useScrollReveal();
  const [tab, setTab] = useState('drivers');
  const maxDriverPts = standings.drivers?.[0]?.points || 1;
  const maxConstrPts = standings.constructors?.[0]?.points || 1;

  return (
    <section ref={ref} className="max-w-6xl mx-auto px-4 py-16 border-t border-zinc-900">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-1 h-6 bg-f1red rounded-full" />
          <h2 className="section-title">Standings F1 {SEASON}</h2>
          {standings.racesCompleted > 0 && (
            <span className="text-[10px] text-zinc-500 font-bold">{standings.racesCompleted} GP</span>
          )}
        </div>
        <Link to="/standings" className="text-sm text-zinc-400 hover:text-white transition-colors">
          Voir tout →
        </Link>
      </div>

      {/* Mini tab toggle */}
      <div className="flex gap-2 mb-5">
        {[['drivers', '🏎 Pilotes'], ['constructors', '🏭 Constructeurs']].map(([t, label]) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${tab === t ? 'bg-f1red text-white' : 'bg-zinc-800 text-zinc-500 hover:text-white'}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'drivers' ? (
        <div className="flex flex-col gap-1.5">
          {standings.drivers.slice(0, 6).map((d, i) => {
            const color = teamColor(d.team);
            const imgUrl = driverImgByNumber(d.number);
            const posColor = i === 0 ? '#FFD600' : i === 1 ? '#A8A8B3' : i === 2 ? '#CD7F32' : '#52525b';
            const gap = i === 0 ? null : d.points - standings.drivers[0].points;
            return (
              <Link to={`/driver/${d.number}`} key={d.number}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-zinc-800/50 transition-all group relative overflow-hidden"
                style={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? 'translateX(0)' : 'translateX(-12px)',
                  transition: `opacity 400ms ${i * 50}ms, transform 400ms ${i * 50}ms`,
                }}>
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                  style={{ background: `linear-gradient(90deg, ${color}0a 0%, transparent 50%)` }} />

                <span className="text-sm font-black w-5 text-center shrink-0" style={{ color: posColor }}>{i + 1}</span>

                <div className="w-8 h-8 rounded-lg overflow-hidden shrink-0"
                  style={{ background: color + '20', border: `1.5px solid ${color}44` }}>
                  {imgUrl && <img src={imgUrl} alt={d.name}
                    className="w-full h-full object-cover object-top" onError={e => e.target.style.display = 'none'} />}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-bold truncate leading-tight group-hover:text-f1red transition-colors">{d.name}</p>
                  <p className="text-[10px] truncate font-medium" style={{ color }}>{d.team}</p>
                  <AnimatedBar value={d.points} max={maxDriverPts} color={color} delay={i * 50} />
                </div>

                <div className="text-right shrink-0 relative">
                  <p className="text-white font-black text-sm">{d.points}</p>
                  {gap !== null && <p className="text-[10px] text-zinc-600 font-mono">{gap}</p>}
                </div>
              </Link>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          {standings.constructors.slice(0, 6).map((c, i) => {
            const color = teamColor(c.name);
            const posColor = i === 0 ? '#FFD600' : i === 1 ? '#A8A8B3' : i === 2 ? '#CD7F32' : '#52525b';
            return (
              <div key={c.name} className="flex items-center gap-3 px-3 py-2.5 rounded-xl relative overflow-hidden"
                style={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? 'translateX(0)' : 'translateX(-12px)',
                  transition: `opacity 400ms ${i * 50}ms, transform 400ms ${i * 50}ms`,
                }}>
                <div className="absolute left-0 top-0 bottom-0 w-0.5 rounded-l" style={{ background: color }} />
                <span className="text-sm font-black w-5 text-center shrink-0 pl-2" style={{ color: posColor }}>{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-bold truncate">{c.name}</p>
                  <AnimatedBar value={c.points} max={maxConstrPts} color={color} delay={i * 50} />
                </div>
                <div className="text-right shrink-0">
                  <p className="text-white font-black text-sm">{c.points}</p>
                  {c.wins > 0 && <p className="text-[10px] text-yellow-400">{c.wins}V</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

const MiniAvatar = ({ user }) => {
  const [failed, setFailed] = useState(false);
  const initials = (user?.username || '?').slice(0, 2).toUpperCase();
  if (user?.avatar && !failed)
    return <img src={user.avatar} alt={user.username}
      className="w-9 h-9 rounded-full object-cover bg-zinc-800 shrink-0"
      onError={() => setFailed(true)} />;
  return (
    <div className="w-9 h-9 rounded-full bg-f1red/20 border border-f1red/40 flex items-center justify-center shrink-0">
      <span className="text-f1red font-black text-xs">{initials}</span>
    </div>
  );
};

const Home = () => {
  const { user } = useAuth();
  const [nextRace, setNextRace] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [standings, setStandings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [randomPool] = useState(() => fisherYates(F1_DRIVERS));
  const [hasPronostic, setHasPronostic] = useState(null);

  const rightDriver = useMemo(() => {
    if (user?.favoriteDriver) {
      return F1_DRIVERS.find(d => d.name === user.favoriteDriver.name) || user.favoriteDriver;
    }
    return randomPool[0];
  }, [user?.favoriteDriver?.name, randomPool]);

  const leftDriver = useMemo(() => {
    for (const d of randomPool) {
      if (d.name !== rightDriver?.name) return d;
    }
    return randomPool[1];
  }, [rightDriver?.name, randomPool]);

  const countdown = useCountdown(
    nextRace?.sessions?.race?.date
      ? `${nextRace.sessions.race.date}T${nextRace.sessions.race.time || '12:00:00Z'}`
      : null
  );

  useEffect(() => {
    Promise.all([
      api.get(`/races?season=${SEASON}`),
      api.get('/leaderboard').catch(() => ({ data: [] })),
      api.get(`/races/standings?season=${SEASON}`).catch(() => ({ data: null })),
    ]).then(([racesRes, lbRes, standingsRes]) => {
      if (standingsRes.data) setStandings(standingsRes.data);
      const now = new Date();
      const upcoming = racesRes.data.filter(r =>
        r.sessions?.race?.date &&
        new Date(`${r.sessions.race.date}T${r.sessions.race.time || '12:00:00Z'}`) >= now &&
        !CANCELLED_RACES.has(r.circuit?.country)
      );
      const nr = upcoming[0] || null;
      setNextRace(nr);
      setLeaderboard((lbRes.data || []).slice(0, 3));
      if (user && nr) {
        api.get(`/pronostics/${nr._id}/me`)
          .then(() => setHasPronostic(true))
          .catch(() => setHasPronostic(false));
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [user?.id]);

  return (
    <div className="overflow-x-hidden">

      {/* ── HERO ── */}
      <section className="relative min-h-[88vh] flex flex-col items-center justify-center grid-bg overflow-hidden px-4">

        {/* Speed lines */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none select-none">
          {[12, 30, 52, 72, 88].map((top, i) => (
            <div key={i} className="absolute h-px"
              style={{
                top: `${top}%`, left: '-5%', right: '-5%',
                background: `linear-gradient(90deg, transparent 0%, rgba(225,6,0,${0.06 + i * 0.02}) 40%, rgba(225,6,0,${0.08 + i * 0.02}) 50%, transparent 100%)`,
                animation: `speed-line ${2.5 + i * 0.6}s ease-out infinite`,
                animationDelay: `${i * 0.9}s`,
              }} />
          ))}
        </div>

        {/* Floating driver numbers background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none select-none">
          {BG_NUMBERS.map((n, i) => (
            <span
              key={i}
              className="absolute font-black text-white/[0.03]"
              style={{
                fontSize: `${120 + (i % 3) * 60}px`,
                left: `${(i * 11) % 95}%`,
                top: `${(i * 17) % 85}%`,
                animation: `float ${4 + (i % 3)}s ease-in-out infinite`,
                animationDelay: `${i * 0.4}s`,
              }}
            >
              {n}
            </span>
          ))}
        </div>

        {/* Driver LEFT */}
        <div className="absolute left-0 bottom-0 hidden lg:block pointer-events-none select-none overflow-hidden animate-slide-from-left"
          style={{ width: '380px', height: '92vh' }}>
          <div className="absolute bottom-0 left-0 w-full h-3/4"
            style={{ background: `radial-gradient(ellipse 80% 60% at 30% 100%, ${leftDriver.color}40 0%, transparent 70%)` }} />
          <img
            src={driverImgUrl(leftDriver.img)}
            alt={leftDriver.name}
            className="absolute bottom-0 left-0 z-10"
            style={{ height: '85vh', width: 'auto', maxWidth: '100%', objectFit: 'contain', objectPosition: 'bottom', filter: 'brightness(0.9) contrast(1.05)' }}
            onError={e => { e.target.style.display = 'none'; }}
          />
          <div className="absolute inset-0 z-20" style={{ background: 'linear-gradient(to right, rgba(9,9,11,0.05) 0%, rgba(9,9,11,0.9) 90%, #09090b 100%)' }} />
          <div className="absolute inset-0 z-20" style={{ background: 'linear-gradient(to bottom, #09090b 0%, transparent 10%)' }} />
          <div className="absolute bottom-5 left-5 z-30">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] mb-0.5" style={{ color: leftDriver.color }}>{leftDriver.team} · #{leftDriver.number}</p>
            <p className="text-white font-black text-lg leading-tight">{leftDriver.name}</p>
          </div>
        </div>

        {/* Driver RIGHT */}
        <div className="absolute right-0 bottom-0 hidden lg:block pointer-events-none select-none overflow-hidden animate-slide-from-right"
          style={{ width: '380px', height: '92vh' }}>
          <div className="absolute bottom-0 right-0 w-full h-3/4"
            style={{ background: `radial-gradient(ellipse 80% 60% at 70% 100%, ${rightDriver.color}40 0%, transparent 70%)` }} />
          <img
            src={driverImgUrl(rightDriver.img)}
            alt={rightDriver.name}
            className="absolute bottom-0 right-0 z-10"
            style={{ height: '85vh', width: 'auto', maxWidth: '100%', objectFit: 'contain', objectPosition: 'bottom', filter: 'brightness(0.9) contrast(1.05)' }}
            onError={e => { e.target.style.display = 'none'; }}
          />
          <div className="absolute inset-0 z-20" style={{ background: 'linear-gradient(to left, rgba(9,9,11,0.05) 0%, rgba(9,9,11,0.9) 90%, #09090b 100%)' }} />
          <div className="absolute inset-0 z-20" style={{ background: 'linear-gradient(to bottom, #09090b 0%, transparent 10%)' }} />
          <div className="absolute bottom-5 right-5 z-30 text-right">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] mb-0.5" style={{ color: rightDriver.color }}>{rightDriver.team} · #{rightDriver.number}</p>
            <p className="text-white font-black text-lg leading-tight">{rightDriver.name}</p>
          </div>
        </div>

        {/* Red glow behind title */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-f1red/10 rounded-full blur-[100px] pointer-events-none" />

        <div className="relative z-10 text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-f1red/10 border border-f1red/30 rounded-full px-4 py-1.5 text-f1red text-sm font-semibold mb-6 animate-fade-in">
            <span className="w-2 h-2 rounded-full bg-f1red animate-pulse inline-block" />
            Saison {SEASON} · En course
          </div>

          <div className="flex justify-center mb-8 animate-fade-up">
            <img
              src="/logo-P1-blanc.png"
              alt="P1 or Not P1"
              className="h-48 md:h-64 w-auto object-contain"
              style={{ filter: 'drop-shadow(0 0 40px rgba(225,6,0,0.4))' }}
            />
          </div>
          <p className="text-zinc-400 text-lg md:text-xl max-w-lg mx-auto mb-8 animate-fade-up delay-200">
            Le jeu de pronos F1 de ta bande. Prouve que t'as les yeux en face des courses.
          </p>

          <div className="flex flex-wrap gap-3 justify-center animate-fade-up delay-300">
            {user ? (
              <>
                <Link to="/calendar" className="btn-primary px-7 py-3 text-base glow-red">
                  Faire mes pronos →
                </Link>
                <Link to="/profile" className="btn-secondary px-7 py-3 text-base">
                  Mon profil
                </Link>
              </>
            ) : (
              <>
                <Link to="/register" className="btn-primary px-7 py-3 text-base glow-red">
                  Rejoindre le jeu →
                </Link>
                <Link to="/calendar" className="btn-secondary px-7 py-3 text-base">
                  Voir le calendrier
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 animate-float-slow">
          <span className="text-zinc-600 text-xs tracking-widest uppercase">Scroll</span>
          <svg className="w-4 h-4 text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </section>

      {/* ── NEXT RACE ── */}
      {!loading && nextRace && (
        <section className="max-w-6xl mx-auto px-4 py-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-6 bg-f1red rounded-full" />
            <h2 className="section-title">Prochain Grand Prix</h2>
            <span className="badge bg-f1red/10 text-f1red border border-f1red/30">Round {nextRace.round}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card flex flex-col gap-4">
              <div>
                <p className="text-zinc-500 text-xs uppercase tracking-widest font-medium">{nextRace.circuit?.country}</p>
                <h3 className="text-3xl font-black text-white mt-1 leading-tight">{nextRace.raceName}</h3>
                <p className="text-zinc-400 text-sm mt-1">{nextRace.circuit?.circuitName} · {nextRace.circuit?.locality}</p>
              </div>

              {countdown && (
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-widest mb-2">Départ dans</p>
                  <div className="flex gap-2">
                    <CountUnit value={countdown.d} label="jours" />
                    <CountUnit value={countdown.h} label="heures" />
                    <CountUnit value={countdown.m} label="min" />
                    <CountUnit value={countdown.s} label="sec" />
                  </div>
                </div>
              )}

              <div className="border-t border-zinc-800 pt-3">
                <SessionRow label="Essais Libres 1" session={nextRace.sessions?.fp1} />
                <SessionRow label="Essais Libres 2" session={nextRace.sessions?.fp2} />
                <SessionRow label="Essais Libres 3" session={nextRace.sessions?.fp3} />
                <SessionRow label="Sprint" session={nextRace.sessions?.sprint} />
                <SessionRow label="Qualifications" session={nextRace.sessions?.qualifying} />
                <SessionRow label="Course" session={nextRace.sessions?.race} />
              </div>

              <Link to={`/grand-prix/${nextRace._id}/pronostic`} className="btn-primary py-3 text-center">
                Poser mes pronos pour ce GP →
              </Link>
            </div>

            <CircuitMap circuit={nextRace.circuit} className="h-64 lg:h-auto min-h-[260px]" />
          </div>
        </section>
      )}

      <HowItWorksSection />

      {/* ── STANDINGS TICKER ── */}
      <StandingsTicker standings={standings} />

      {/* ── LEADERBOARD PREVIEW ── */}
      {leaderboard.length > 0 && (
        <LeaderboardPodium leaderboard={leaderboard} />
      )}

      {/* ── BOX-BOX GAMES ── */}
      <BoxBoxSection />

      {/* ── F1 STANDINGS PREVIEW ── */}
      {standings?.drivers?.length > 0 && (
        <StandingsPreview standings={standings} />
      )}

      {/* ── CTA BOTTOM ── */}
      {!user && (
        <section className="relative overflow-hidden border-t border-zinc-900">
          <div className="absolute inset-0 bg-gradient-to-br from-f1red/10 via-transparent to-transparent pointer-events-none" />
          <div className="max-w-3xl mx-auto px-4 py-20 text-center relative">
            <p className="text-zinc-500 text-sm uppercase tracking-widest mb-3">Prêt à jouer ?</p>
            <h2 className="text-4xl font-black text-white mb-4">Rejoins la bataille.</h2>
            <p className="text-zinc-400 mb-8 text-lg">Inscris-toi en 10 secondes et commence à prouver que tu mérites le P1.</p>
            <Link to="/register" className="btn-primary px-10 py-4 text-lg glow-red">
              Créer mon compte →
            </Link>
          </div>
        </section>
      )}

    </div>
  );
};

export default Home;
